import type {
  ReviewCode,
  ReviewElement,
  ReviewFigure,
  ReviewPosition,
  ReviewSection,
} from "@patent-studio/api-types";
import { Image, Lightbulb, ListTree, Search, Tag } from "lucide-react";
import { FindingList } from "@/components/patterns/FindingList";
import { VerdictBadge } from "@/components/shared/VerdictBadge";
import { FILE_META } from "@/components/ui/marks";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { StatusPill } from "@/components/ui/StatusPill";
import { Thumbnail } from "@/components/ui/Thumbnail";
import type { Rename } from "@/lib/old-names";
import { plural } from "@/lib/plural";
import { OldNameNotes } from "../../figures/OldNameNotes";
import { tagOf } from "../../invention/element-tags";
import { cpcTitle } from "../../intake/ClassificationParts";
import { type EditTarget, ReviewCard } from "./ReviewCard";

const ROW =
  "flex items-start gap-3 border-b border-border-subtle py-2.5 last:border-b-0";
const NONE = "text-sm text-muted-foreground";

/** The elements of the invention, each in its words with the sources it was read from. */
export function InventionCard({
  elements,
  edit,
}: {
  elements: ReviewElement[];
  edit: EditTarget;
}) {
  return (
    <ReviewCard icon={<Lightbulb />} title="Invention" edit={edit}>
      <MicroLabel as="h3" className="mb-2">
        Elements of the Invention
      </MicroLabel>
      {elements.length === 0 ? (
        <p className={NONE}>No element is confirmed yet.</p>
      ) : (
        <ol>
          {elements.map((element) => {
            const tag = tagOf(element.source_tag);
            return (
              <li key={element.element_id} className={ROW}>
                <span className="w-6 flex-none pt-0.5 font-mono text-[11px] font-semibold text-subtle-foreground">
                  {element.number === null ? "" : `${String(element.number)}.`}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px]">{`"${element.text}"`}</p>
                  {element.citations.map((citation) => (
                    <p key={citation} className={FILE_META}>
                      {citation}
                    </p>
                  ))}
                </div>
                <StatusPill tone="inherit" className={tag.chipClass}>
                  {tag.label}
                </StatusPill>
              </li>
            );
          })}
        </ol>
      )}
    </ReviewCard>
  );
}

/** Each novelty position and where it stands against the prior art. */
export function NoveltyCard({
  positions,
  edit,
}: {
  positions: ReviewPosition[];
  edit: EditTarget;
}) {
  const head = "px-2.5 pb-2 text-left font-mono font-medium";
  return (
    <ReviewCard icon={<Search />} title="Novelty" edit={edit}>
      {positions.length === 0 ? (
        <p className={NONE}>No novelty position is defined yet.</p>
      ) : (
        <table className="w-full text-[12.5px]">
          <thead className="border-b border-border">
            <tr>
              <MicroLabel as="th" size="sm" className={`${head} w-32`}>
                Position
              </MicroLabel>
              <MicroLabel as="th" size="sm" className={head}>
                Position name
              </MicroLabel>
              <MicroLabel as="th" size="sm" className={`${head} w-40`}>
                Novelty status
              </MicroLabel>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <tr
                key={position.position_id}
                className="border-b border-border-subtle last:border-b-0"
              >
                <td className="p-2.5 text-muted-foreground">{`Position ${String(position.number)}`}</td>
                <td className="p-2.5 font-medium">{position.title}</td>
                <td className="p-2.5">
                  <VerdictBadge verdict={position.verdict ?? "not_assessed"} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReviewCard>
  );
}

/** The codes the attorney confirmed, each with its title in the scheme. */
export function ClassificationCard({
  codes,
  edit,
}: {
  codes: ReviewCode[];
  edit: EditTarget;
}) {
  return (
    <ReviewCard icon={<Tag />} title="Classifications" edit={edit}>
      {codes.length === 0 ? (
        <p className={NONE}>No classifications have been confirmed yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {codes.map((code) => (
            <li
              key={code.symbol}
              className="flex items-baseline gap-3.5 py-2 first:pt-0 last:pb-0"
            >
              <b className="min-w-[110px] flex-none font-mono text-[12.5px] font-semibold text-primary">
                {code.symbol}
              </b>
              <span className="min-w-0 flex-1 text-[13px] leading-normal text-pretty">
                {cpcTitle(code.title)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </ReviewCard>
  );
}

/** Each placed figure by its label, with its brief description and how many components it numbers. */
export function FiguresCard({
  figures,
  thumbnails,
  words,
  renames,
  edit,
}: {
  figures: ReviewFigure[];
  thumbnails: ReadonlyMap<string, string | undefined>;
  /** Each figure's own words, its brief description and walk-through, which may still use an old name. */
  words: ReadonlyMap<string, string>;
  renames: Rename[];
  edit: EditTarget;
}) {
  return (
    <ReviewCard icon={<Image />} title="Figures" edit={edit}>
      <FindingList
        findings={unnumbered(figures)}
        testId="review-figure-finding"
        className="mb-3"
      />
      {figures.length === 0 ? (
        <p className={NONE}>No figures have been defined yet.</p>
      ) : (
        <ul>
          {figures.map((figure) => (
            <li key={figure.file_id} className={`${ROW} items-center`}>
              <Thumbnail src={thumbnails.get(figure.file_id)} alt="" />
              <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-2.5">
                <b className="text-[12.5px]">FIG. {figure.label}</b>
                <span className="text-xs text-muted-foreground">
                  {figure.description}
                </span>
                {figure.print_note && (
                  <Note tone="warning" className="basis-full">
                    {figure.print_note}
                  </Note>
                )}
                <OldNameNotes
                  text={words.get(figure.file_id) ?? ""}
                  renames={renames}
                  className="mt-0 basis-full"
                />
                {figure.lettering_note && (
                  <Note hint className="basis-full">
                    {figure.lettering_note}
                  </Note>
                )}
              </div>
              {figure.components > 0 && (
                <span className="flex-none text-[11px] text-subtle-foreground">
                  {plural(figure.components, "numbered component")}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </ReviewCard>
  );
}

/**
 * A placed figure with no named components gets no reference numerals in the
 * draft. The run still goes: this is a warning, not a gate.
 */
function unnumbered(figures: readonly ReviewFigure[]) {
  return figures
    .filter((figure) => figure.components === 0)
    .map((figure) => {
      const named = `FIG. ${figure.label}`;
      return {
        kind: "no-components",
        note:
          `${named} has no named components, so the draft gives it no reference numerals. ` +
          "US rules for drawings (37 CFR 1.84(p)) ask for a numeral on each part the description names. " +
          `To add them, name the components of ${named} in Figures.`,
      };
    });
}

/** The Detailed Description framework: its sections in their order. */
export function FrameworkCard({
  sections,
  edit,
}: {
  sections: ReviewSection[];
  edit: EditTarget;
}) {
  return (
    <ReviewCard
      icon={<ListTree />}
      title="Detailed Description framework"
      edit={edit}
    >
      {sections.length === 0 ? (
        <p className={NONE}>No framework is planned yet.</p>
      ) : (
        <ol>
          {sections.map((section) => (
            <li key={section.section_id} className={`${ROW} items-baseline`}>
              <span className="min-w-0 flex-1 text-[13px] font-medium">
                {section.title}
              </span>
              <span className="flex-none text-[11px] text-subtle-foreground">
                {plural(section.subsections, "item")}
              </span>
            </li>
          ))}
        </ol>
      )}
    </ReviewCard>
  );
}
